import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertCircle,
  CalendarDays,
  PartyPopper,
  Palmtree,
  RefreshCw,
  UserCheck,
  Users,
} from "lucide-react";
import { format } from "date-fns";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { DatePickerInput } from "@/components/ui/DatePickerInput";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { fetchDepartmentLeaves } from "@/services/attendanceService";
import { getErrorMessage } from "@/utils/errorHandler";

// Mirrors MAX_DATE_OFFSET_DAYS on the server — the API rejects anything wider.
const MAX_DATE_OFFSET_DAYS = 30;

const DAY_TYPE_LABELS = {
  FULL_DAY: "Full day",
  HALF_DAY: "Half day",
};

const prettyDate = (dateKey) => {
  if (!dateKey) return "";
  const parsed = new Date(`${dateKey}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? dateKey : format(parsed, "EEE, dd MMM yyyy");
};

const shortDate = (dateKey) => {
  if (!dateKey) return "";
  const parsed = new Date(`${dateKey}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? dateKey : format(parsed, "dd MMM");
};

// "27 Jul" for a single day, "27 Jul – 30 Jul" for a range.
const leaveDuration = (entry) => {
  if (!entry?.startDate) return "";
  if (entry.startDate === entry.endDate) return shortDate(entry.startDate);
  return `${shortDate(entry.startDate)} – ${shortDate(entry.endDate)}`;
};

const dayCountLabel = (entry) => {
  if (entry?.dayType === "HALF_DAY") return "0.5 day";
  const days = entry?.totalDays || 1;
  return `${days} ${days === 1 ? "day" : "days"}`;
};

const LeaveRow = ({ entry }) => (
  <li className="flex items-start gap-3 rounded-xl border border-border bg-card p-3 transition-colors hover:bg-muted/40 sm:items-center sm:p-4">
    <Avatar className="h-10 w-10 shrink-0 border border-border">
      {entry.profileImage && <AvatarImage src={entry.profileImage} alt="" />}
      <AvatarFallback className="bg-amber-100 text-xs font-semibold text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
        {entry.initials}
      </AvatarFallback>
    </Avatar>

    <div className="min-w-0 flex-1">
      <p className="truncate font-semibold text-foreground">{entry.name}</p>

      {entry.designation && (
        <p className="truncate text-sm text-muted-foreground">{entry.designation}</p>
      )}

      <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-0.5 font-medium text-foreground/80">
          <Palmtree className="h-3 w-3" aria-hidden="true" />
          {entry.leaveType}
        </span>
        <span aria-hidden="true">•</span>
        <span>{leaveDuration(entry)}</span>
        <span aria-hidden="true">•</span>
        <span>
          {DAY_TYPE_LABELS[entry.dayType] || DAY_TYPE_LABELS.FULL_DAY} ({dayCountLabel(entry)})
        </span>
      </div>
    </div>

    <div className="hidden shrink-0 text-right sm:block">
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Back on</p>
      <p className="text-sm font-medium text-foreground">{shortDate(entry.returnDate)}</p>
    </div>
  </li>
);

const LoadingState = () => (
  <div className="space-y-3" aria-hidden="true">
    {[0, 1, 2].map((row) => (
      <div key={row} className="flex items-center gap-3 rounded-xl border border-border p-4">
        <Skeleton className="h-10 w-10 rounded-full" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-1/3" />
          <Skeleton className="h-3 w-1/2" />
        </div>
      </div>
    ))}
  </div>
);

const EmptyState = ({ teamSize, holiday, isToday, scopeNoun }) => {
  // Only genuinely-empty case left: a one-person team. An employee without a
  // department now falls back to company scope server-side, so the old
  // "ask HR to assign you a department" dead end is gone.
  if (teamSize === 0) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-border px-6 py-10 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted">
          <Users className="h-7 w-7 text-muted-foreground" aria-hidden="true" />
        </div>
        <p className="font-semibold text-foreground">No colleagues to show yet</p>
        <p className="max-w-sm text-sm text-muted-foreground">
          There are no other active employees in {scopeNoun} right now.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-emerald-300/70 bg-emerald-50/60 px-6 py-10 text-center dark:border-emerald-500/30 dark:bg-emerald-900/15">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/40">
        {holiday ? (
          <PartyPopper className="h-7 w-7 text-emerald-700 dark:text-emerald-300" aria-hidden="true" />
        ) : (
          <UserCheck className="h-7 w-7 text-emerald-700 dark:text-emerald-300" aria-hidden="true" />
        )}
      </div>
      <p className="font-semibold text-emerald-900 dark:text-emerald-200">
        Everyone in {scopeNoun} is present {isToday ? "today" : "on this day"}.
      </p>
      <p className="max-w-sm text-sm text-emerald-800/80 dark:text-emerald-300/80">
        {holiday
          ? `${holiday.name} — no approved leave is recorded for this day.`
          : "No approved leave is recorded for this day."}
      </p>
    </div>
  );
};

const ErrorState = ({ message, onRetry }) => (
  <div
    role="alert"
    className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-border px-6 py-10 text-center"
  >
    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted">
      <AlertCircle className="h-7 w-7 text-muted-foreground" aria-hidden="true" />
    </div>
    <p className="font-semibold text-foreground">Could not load team availability</p>
    <p className="max-w-sm text-sm text-muted-foreground">{message}</p>
    <Button variant="outline" size="sm" onClick={onRetry}>
      <RefreshCw className="mr-2 h-4 w-4" aria-hidden="true" />
      Try again
    </Button>
  </div>
);

/**
 * "Team Availability" — a button + modal showing which colleagues are on
 * approved leave.
 *
 * Scope is decided server-side: the employee's own DEPARTMENT when the company
 * uses departments and they are in one, otherwise the whole COMPANY. Either way
 * it never crosses a tenant boundary. `scopeLabel` names whichever applies, so
 * the copy stays honest without the client re-deriving the rule.
 *
 * The button badge always reports TODAY, so its meaning stays stable; the date
 * picker inside the modal only changes the list. Responses are cached per date
 * for the lifetime of the component so flipping between dates is free.
 *
 * Everything shown here comes from GET /attendance/department-leaves, which
 * scopes the data to the caller's company and department server-side and never
 * returns leave reasons, documents or comments.
 */
const TeamAvailability = ({ className = "" }) => {
  const [open, setOpen] = useState(false);

  // Today's snapshot — drives the button badge and the inline summary line.
  const [today, setToday] = useState(null);
  const [todayLoading, setTodayLoading] = useState(true);
  const [todayError, setTodayError] = useState("");

  // The date currently shown inside the modal ("yyyy-MM-dd").
  const [selectedDate, setSelectedDate] = useState(null);
  const [view, setView] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewError, setViewError] = useState("");

  const cacheRef = useRef(new Map());
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // "Today" per the COMPANY's timezone, as reported by the API. Falls back to
  // the browser's date only while the first request is in flight.
  const todayKey = today?.date || format(new Date(), "yyyy-MM-dd");

  // Caches the in-flight promise, not just the result, so opening the modal
  // while the badge is still loading reuses that request instead of firing a
  // second one. A failed request is evicted so "Try again" really retries.
  const load = useCallback((dateKey) => {
    const key = dateKey || "@today";
    const cached = cacheRef.current.get(key);
    if (cached) return cached;

    const request = fetchDepartmentLeaves(dateKey)
      .then((payload) => {
        // Key today's payload by its resolved date too, so picking today in the
        // date picker lands on the same entry.
        if (payload?.date) cacheRef.current.set(payload.date, request);
        return payload;
      })
      .catch((error) => {
        cacheRef.current.delete(key);
        throw error;
      });

    cacheRef.current.set(key, request);
    return request;
  }, []);

  const loadToday = useCallback(async () => {
    setTodayLoading(true);
    setTodayError("");
    try {
      const payload = await load(null);
      if (!isMountedRef.current) return;
      setToday(payload);
    } catch (error) {
      if (!isMountedRef.current) return;
      setTodayError(getErrorMessage(error, "Failed to load team availability"));
    } finally {
      if (isMountedRef.current) setTodayLoading(false);
    }
  }, [load]);

  useEffect(() => {
    loadToday();
  }, [loadToday]);

  const loadDate = useCallback(
    async (dateKey) => {
      setViewLoading(true);
      setViewError("");
      try {
        const payload = await load(dateKey === todayKey ? null : dateKey);
        if (!isMountedRef.current) return;
        setView(payload);
      } catch (error) {
        if (!isMountedRef.current) return;
        setView(null);
        setViewError(getErrorMessage(error, "Failed to load team availability"));
      } finally {
        if (isMountedRef.current) setViewLoading(false);
      }
    },
    [load, todayKey],
  );

  const openModal = () => {
    setOpen(true);
    // Always open on today, whatever date was last inspected.
    setSelectedDate(todayKey);
    setViewError("");

    if (today) {
      setView(today);
      setViewLoading(false);
    } else {
      loadDate(todayKey);
    }
  };

  const onDateChange = (dateKey) => {
    if (!dateKey || dateKey === selectedDate) return;
    setSelectedDate(dateKey);
    loadDate(dateKey);
  };

  const onOpenChange = (nextOpen) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      // Refresh today's badge in case the modal surfaced newer data.
      if (view?.isToday) setToday(view);
    }
  };

  const todayCount = today?.onLeaveCount || 0;
  const hasBadge = todayCount > 0;

  const datePickerBounds = useMemo(() => {
    const min = new Date();
    min.setDate(min.getDate() - MAX_DATE_OFFSET_DAYS);
    return { minDate: min, maxDays: MAX_DATE_OFFSET_DAYS * 2 };
  }, []);

  const summaryLine = todayLoading
    ? "Checking who is on leave today…"
    : todayError
      ? "Tap to retry"
      : hasBadge
          ? `${todayCount} ${todayCount === 1 ? "teammate" : "teammates"} on leave today`
          : "Everyone is present today";

  const buttonLabel = todayLoading
    ? "Team availability — loading"
    : hasBadge
      ? `Team availability — ${todayCount} ${
          todayCount === 1 ? "teammate" : "teammates"
        } from your department on leave today`
      : "Team availability — everyone in your department is present today";

  const viewIsToday = Boolean(view?.isToday ?? selectedDate === todayKey);

  // "the Engineering team" / "Acme Corp" — reads naturally inside a sentence.
  const scopeNoun =
    view?.scope === "DEPARTMENT"
      ? `the ${view.scopeLabel} team`
      : view?.scopeLabel || "your company";

  return (
    <div className={className}>
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="outline"
              onClick={openModal}
              aria-label={buttonLabel}
              aria-haspopup="dialog"
              // Deliberately a plain secondary action, same height/padding as the
              // neighbouring "Log OT". Approved leave is information, not a
              // problem, so the only colour in the control is the count badge.
              // Neutral hover on purpose: --accent is the brand teal in this
              // theme, not a grey, so `hover:bg-accent` turned the button teal
              // and hid the muted icon. `bg-muted` is the surface that
              // muted-foreground is designed to sit on.
              className="group w-full hover:bg-muted sm:w-auto"
            >
              <Users
                className="h-4 w-4 text-muted-foreground transition-colors group-hover:text-foreground"
                aria-hidden="true"
              />
              Team Availability
              {hasBadge && (
                <span
                  aria-hidden="true"
                  className="-mr-0.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-amber-400 px-1 text-[10px] font-bold leading-none text-amber-950"
                >
                  {todayCount}
                </span>
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {/* The at-a-glance summary lives here now, so the trigger can stay
                the same size as every other header action. */}
            <p className="font-medium text-foreground">{summaryLine}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              View colleagues from your department who are on approved leave.
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {/* Announced to screen readers only — sighted users get the badge. */}
      <span className="sr-only" aria-live="polite">
        {summaryLine}
      </span>

      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="flex max-h-[90vh] flex-col overflow-hidden border border-border/60 bg-background p-0 shadow-2xl sm:max-w-2xl">
          <DialogHeader className="border-b border-border px-5 py-5 sm:px-6">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-amber-100 text-amber-700 ring-1 ring-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:ring-amber-500/30">
                <Users className="h-5 w-5" aria-hidden="true" />
              </div>
              <div className="space-y-1 text-left">
                <DialogTitle className="text-xl font-semibold text-foreground">
                  Team Availability
                </DialogTitle>
                <DialogDescription className="text-sm text-muted-foreground">
                  Colleagues from{" "}
                  <span className="font-medium text-foreground">
                    {view?.scopeLabel || today?.scopeLabel || "your team"}
                  </span>{" "}
                  who are on approved leave.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="flex flex-col gap-3 border-b border-border bg-muted/20 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
            <div>
              <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                Showing
              </p>
              <p className="text-sm font-semibold text-foreground">
                {prettyDate(selectedDate)}
                {viewIsToday && (
                  <span className="ml-2 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
                    Today
                  </span>
                )}
              </p>
            </div>

            <div
              role="group"
              aria-label={`Select a date within ${MAX_DATE_OFFSET_DAYS} days of today`}
              className="w-full sm:w-[220px]"
            >
              <DatePickerInput
                value={selectedDate}
                onChange={onDateChange}
                minDate={datePickerBounds.minDate}
                maxDays={datePickerBounds.maxDays}
                className="h-9 border-border bg-background"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-5 py-5 sm:px-6">
            {viewLoading ? (
              <LoadingState />
            ) : viewError ? (
              <ErrorState message={viewError} onRetry={() => loadDate(selectedDate)} />
            ) : view?.onLeaveCount > 0 ? (
              <>
                <div className="mb-3 flex items-center justify-between gap-2">
                  <p className="text-sm font-medium text-foreground">
                    {view.onLeaveCount}{" "}
                    {view.onLeaveCount === 1 ? "colleague" : "colleagues"} on leave
                    {view.truncated && (
                      <span className="ml-1 font-normal text-muted-foreground">
                        (showing first {view.employees.length})
                      </span>
                    )}
                  </p>
                  {view.holiday && (
                    <span className="inline-flex items-center gap-1 rounded-md bg-sky-100/70 px-2 py-0.5 text-xs font-medium text-sky-800 dark:bg-sky-900/25 dark:text-sky-300">
                      <PartyPopper className="h-3 w-3" aria-hidden="true" />
                      {view.holiday.name}
                    </span>
                  )}
                </div>
                <ul className="space-y-2.5">
                  {view.employees.map((entry) => (
                    <LeaveRow key={entry.employeeId} entry={entry} />
                  ))}
                </ul>
              </>
            ) : (
              <EmptyState
                teamSize={view?.teamSize || 0}
                holiday={view?.holiday}
                isToday={viewIsToday}
                scopeNoun={scopeNoun}
              />
            )}
          </div>

          <div className="flex items-center gap-2 border-t border-border px-5 py-3 text-xs text-muted-foreground sm:px-6">
            <CalendarDays className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span>
              Approved leave only. Leave reasons and documents are never shown here.
            </span>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamAvailability;
